import { useState, useEffect, useCallback } from 'react';
import { Globe, Plus, ExternalLink, RefreshCw, Trash2, Edit2, Loader2, AlertCircle } from 'lucide-react';
import api, { LandingPageConfig, LandingPageInput } from '../services/api';
import { useClient } from '../contexts/ClientContext';

const emptyForm: LandingPageInput = {
  subdomain: '',
  business_name: '',
  tagline: '',
  phone: '',
  phone_display: '',
  email: '',
  services: [],
  service_areas: [],
  cta_text: 'Get a Free Estimate',
  primary_color: '#0ea5e9',
  primary_color_dark: '#0284c7',
};

const LandingPages = () => {
  const { selectedClientId, selectedClient } = useClient();
  const [landingPages, setLandingPages] = useState<LandingPageConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<LandingPageInput>(emptyForm);
  const [servicesInput, setServicesInput] = useState('');
  const [areasInput, setAreasInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [building, setBuilding] = useState<string | null>(null);

  const loadLandingPages = useCallback(async () => {
    if (!selectedClientId) {
      setLandingPages([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await api.getClientLandingPages(selectedClientId);
      setLandingPages(data);
    } catch (err) {
      console.error('Failed to load landing pages:', err);
      setError('Failed to load landing pages');
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  useEffect(() => {
    loadLandingPages();
  }, [loadLandingPages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId) return;
    setSaving(true);
    try {
      const data = {
        ...form,
        services: servicesInput.split(',').map(s => s.trim()).filter(Boolean),
        service_areas: areasInput.split(',').map(s => s.trim()).filter(Boolean),
      };
      if (editingId) {
        await api.updateLandingPage(selectedClientId, editingId, data);
      } else {
        await api.createLandingPage(selectedClientId, data);
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      setServicesInput('');
      setAreasInput('');
      loadLandingPages();
    } catch (err) {
      console.error('Failed to save landing page:', err);
      alert('Failed to save landing page');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (lp: LandingPageConfig) => {
    setForm({
      subdomain: lp.subdomain,
      business_name: lp.business_name,
      tagline: lp.tagline,
      phone: lp.phone,
      phone_display: lp.phone_display,
      email: lp.email || '',
      services: lp.services,
      service_areas: lp.service_areas,
      cta_text: lp.cta_text,
      primary_color: lp.primary_color,
      primary_color_dark: lp.primary_color_dark,
    });
    setServicesInput(lp.services.join(', '));
    setAreasInput(lp.service_areas.join(', '));
    setEditingId(lp.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!selectedClientId || !window.confirm('Delete this landing page?')) return;
    try {
      await api.deleteLandingPage(selectedClientId, id);
      loadLandingPages();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleBuild = async (id: string) => {
    if (!selectedClientId) return;
    try {
      setBuilding(id);
      await api.buildLandingPage(selectedClientId, id);
      
      // Poll until build completes (max 60 seconds)
      const pollInterval = setInterval(async () => {
        try {
          const pages = await api.getClientLandingPages(selectedClientId);
          const page = pages.find(p => p.id === id);
          if (page && page.build_status !== 'building') {
            clearInterval(pollInterval);
            setBuilding(null);
            setLandingPages(pages);
          }
        } catch (e) {
          console.error('Poll error:', e);
        }
      }, 2000);
      
      // Safety timeout to stop polling after 60s
      setTimeout(() => {
        clearInterval(pollInterval);
        setBuilding(null);
        loadLandingPages();
      }, 60000);
      
    } catch (err) {
      console.error('Failed to trigger build:', err);
      alert('Failed to trigger build');
      setBuilding(null);
    }
  };

  const openNewForm = () => {
    setForm({
      ...emptyForm,
      business_name: selectedClient?.name || '',
    });
    setServicesInput('');
    setAreasInput('');
    setEditingId(null);
    setShowForm(true);
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      building: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 dark:bg-gray-800'}`}>{status}</span>;
  };

  if (!selectedClientId) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Globe className="w-6 h-6 text-primary-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Landing Pages</h1>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
          <p className="text-yellow-800 dark:text-yellow-200">Please select a client from the dropdown to manage their landing pages.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Globe className="w-6 h-6 text-primary-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Landing Pages</h1>
        </div>
        <button
          onClick={openNewForm}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> New Landing Page
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {showForm && (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {editingId ? 'Edit Landing Page' : 'Create New Landing Page'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subdomain</label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={form.subdomain}
                    onChange={e => setForm({...form, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                    className="input w-full"
                    placeholder="mycompany"
                    required
                    disabled={!!editingId}
                  />
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">.digimint.co</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Business Name</label>
                <input
                  type="text"
                  value={form.business_name}
                  onChange={e => setForm({...form, business_name: e.target.value})}
                  className="input w-full"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tagline</label>
                <input
                  type="text"
                  value={form.tagline}
                  onChange={e => setForm({...form, tagline: e.target.value})}
                  className="input w-full"
                  placeholder="Quality Service You Can Trust"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone (E.164 format)</label>
                <input
                  type="text"
                  value={form.phone}
                  onChange={e => setForm({...form, phone: e.target.value})}
                  className="input w-full"
                  placeholder="+19045551234"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Display</label>
                <input
                  type="text"
                  value={form.phone_display}
                  onChange={e => setForm({...form, phone_display: e.target.value})}
                  className="input w-full"
                  placeholder="(904) 555-1234"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CTA Button Text</label>
                <input
                  type="text"
                  value={form.cta_text}
                  onChange={e => setForm({...form, cta_text: e.target.value})}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Services (comma-separated)</label>
                <input
                  type="text"
                  value={servicesInput}
                  onChange={e => setServicesInput(e.target.value)}
                  className="input w-full"
                  placeholder="Kitchens, Bathrooms, Additions"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Service Areas (comma-separated)</label>
                <input
                  type="text"
                  value={areasInput}
                  onChange={e => setAreasInput(e.target.value)}
                  className="input w-full"
                  placeholder="Tampa, Orlando, Jacksonville"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Primary Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.primary_color}
                    onChange={e => setForm({...form, primary_color: e.target.value})}
                    className="w-12 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                  />
                  <input
                    type="text"
                    value={form.primary_color}
                    onChange={e => setForm({...form, primary_color: e.target.value})}
                    className="input flex-1"
                    placeholder="#0ea5e9"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Primary Dark Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.primary_color_dark}
                    onChange={e => setForm({...form, primary_color_dark: e.target.value})}
                    className="w-12 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                  />
                  <input
                    type="text"
                    value={form.primary_color_dark}
                    onChange={e => setForm({...form, primary_color_dark: e.target.value})}
                    className="input flex-1"
                    placeholder="#0284c7"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="btn btn-ghost">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      ) : landingPages.length === 0 && !showForm ? (
        <div className="text-center py-12 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
          <Globe className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No landing pages yet</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">Create a landing page to start capturing leads.</p>
          <button onClick={openNewForm} className="btn btn-primary">
            <Plus className="w-4 h-4 mr-2" /> Create Landing Page
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {landingPages.map(lp => (
            <div key={lp.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-5">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{lp.business_name}</h3>
                  <a
                    href={`https://${lp.subdomain}.digimint.co`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-500 hover:text-primary-600 flex items-center gap-1 text-sm mt-1"
                  >
                    {lp.subdomain}.digimint.co <ExternalLink className="w-3 h-3" />
                  </a>
                  {lp.tagline && <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{lp.tagline}</p>}
                </div>
                <div className="flex items-center gap-3">
                  {statusBadge(lp.build_status)}
                  <button
                    onClick={() => handleBuild(lp.id)}
                    className="btn btn-ghost btn-sm"
                    title="Build & Deploy"
                    disabled={building === lp.id}
                  >
                    {building === lp.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </button>
                  <button onClick={() => handleEdit(lp)} className="btn btn-ghost btn-sm" title="Edit">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(lp.id)} className="btn btn-ghost btn-sm text-red-500 hover:text-red-600" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                {lp.phone_display && <span>Phone: {lp.phone_display}</span>}
                {lp.services.length > 0 && <span>Services: {lp.services.join(', ')}</span>}
                {lp.last_built_at && (
                  <span>Last built: {new Date(lp.last_built_at).toLocaleString()}</span>
                )}
              </div>
              {lp.build_error && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-700 dark:text-red-300">
                  {lp.build_error}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LandingPages;
